import { useCallback, useEffect, useState } from 'react';

export type EditSessionProfile = {
  type: 'vendor' | 'activity';
  id: string;
  slug: string;
  name: string;
};

export type EditSession = {
  sessionToken: string;
  expiresAt: string; // ISO
  profiles: EditSessionProfile[];
};

const STORAGE_KEY = 'profestival.editSession';

function readSession(): EditSession | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as EditSession;
    if (!parsed.sessionToken || !parsed.expiresAt) return null;
    if (new Date(parsed.expiresAt).getTime() <= Date.now()) {
      window.localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function useEditSession() {
  const [session, setSession] = useState<EditSession | null>(() => readSession());

  useEffect(() => {
    // Sync across tabs.
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) setSession(readSession());
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const saveSession = useCallback((next: EditSession) => {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    setSession(next);
  }, []);

  const clearSession = useCallback(() => {
    window.localStorage.removeItem(STORAGE_KEY);
    setSession(null);
  }, []);

  return { session, saveSession, clearSession };
}
